import { EditorialClosing } from '~/ui-rocco/pages/detail-shared';
import { DetailScrollReveal } from '~/ui-rocco/pages/detail-shared';
import type { PageBuilderModuleType } from '@modules/page-builder/types';
import type { EnquiryModalProfessional } from '~/modules/enquiry/hooks/use-enquiry-modal';
import type {
    ArticleRevampVariant,
    RevampArticleTile,
    RevampCredit,
    RevampDownload,
    RevampHeaderCategory,
} from '../type';
import { RevampModules } from './revamp-modules/RevampModules';
import { RevampNewsletterSignup } from './RevampNewsletterSignup';
import { RevampHeader } from './RevampHeader';
import { RevampLogomark } from './RevampLogomark';
import { RevampTeaser } from './RevampTeaser';
import { RevampHeroImage, type RevampHeroImageProps } from './RevampHeroImage';
import { RevampThemesCredits } from './RevampThemesCredits';
import { RevampDiscoverMore } from './RevampDiscoverMore';
import { RevampAuthorRail, type RevampAuthor } from './RevampAuthorRail';
import {
    RevampPremiumTitleSection,
    type RevampPremiumTitleSectionProps,
} from './RevampPremiumTitleSection';
import { RevampFlexiTitleSection } from './RevampFlexiTitleSection';

export interface ArticleRevampPageProps {
    variant: ArticleRevampVariant;
    header: {
        label?: string;
        title: string;
        categories: RevampHeaderCategory[];
    };
    teaser?: string;
    hero?: RevampHeroImageProps;
    credits: RevampCredit[];
    professional?: { title: string; link: string };
    discoverTiles: RevampArticleTile[];
    /** The article's guide files; the accordion row is omitted when there are none. */
    downloads?: RevampDownload[];
    /** Article the download event is attributed to. */
    articleID: number;
    /** The article's own path, shared by the Themes & Credits social buttons. */
    shareLink: string;
    /** Full professional behind the inline enquiry row. */
    enquiryProfessional?: EnquiryModalProfessional;
    author?: RevampAuthor;
    modules?: PageBuilderModuleType[];
    /** When true, truncate body modules and omit closing sections (auth banner is rendered by the route). */
    articleGatingEnabled?: boolean;
    /**
     * When set, the premium editorial title section replaces the standard
     * author rail + header + teaser + hero stack (Figma 2479:73336).
     */
    premiumHero?: RevampPremiumTitleSectionProps;
    /**
     * When true (and a banner has been uploaded), the flexi split title
     * section replaces the same stack (Figma 2738:57368). It reuses the props
     * the standard stack already receives — banner, headline, categories and
     * teaser — so it needs no view model of its own. Premium takes precedence
     * if both tiers are somehow unlocked.
     */
    flexiTitle?: boolean;
    /**
     * Show the newsletter subscribe band below the first body module. Author
     * toggle, off by default (Article.ShowNewsletterSignup in BM).
     */
    showNewsletterSignup?: boolean;
    /**
     * Publish date for the byline. Premium renders it in its own credits row,
     * so this feeds the standard and flexi title sections.
     */
    publishDate?: string;
}

/**
 * Composes the revamped article page sections. The variant decides which
 * tier-gated sections render; standard shows the core editorial sections only.
 * Auth gating (GrowthBook `article-gating`) mirrors the legacy article tree:
 * body modules are truncated/blurred and post-body sections are omitted so the
 * sign-in banner can prompt unauthenticated readers.
 */
export const ArticleRevampPage = ({
    header,
    teaser,
    hero,
    credits,
    professional,
    discoverTiles,
    author,
    modules,
    downloads = [],
    articleID,
    shareLink,
    enquiryProfessional,
    articleGatingEnabled = false,
    premiumHero,
    flexiTitle = false,
    showNewsletterSignup = false,
    publishDate,
}: ArticleRevampPageProps) => {
    const renderTitleSection = () => {
        if (premiumHero) {
            return (
                <div className="-mt-10 w-full md:-mt-12">
                    <RevampPremiumTitleSection {...premiumHero} />
                </div>
            );
        }

        if (flexiTitle && hero) {
            return (
                // The design leaves 50px between this section and the first
                // body module rather than main's 180px, so claw the difference
                // back here instead of making the gap conditional.
                <div className="flex w-full flex-col md:-mb-[8.125rem] md:gap-[3.12rem]">
                    <RevampLogomark />
                    <RevampFlexiTitleSection
                        imageUrl={hero.imageUrl}
                        alt={hero.alt}
                        title={header.title}
                        sponsoredBy={professional?.title}
                        categories={header.categories}
                        teaser={teaser}
                        publishDate={publishDate}
                    />
                </div>
            );
        }

        return (
            <>
                {author && <RevampAuthorRail {...author} />}
                {/* Mobile runs headline block → hero → teaser, so the reader
                    meets the image before the intro copy; desktop keeps the
                    design's headline → teaser → hero column. Reordered rather
                    than rendered twice, so the teaser stays a single node in
                    the document. Desktop's teaser → hero distance is the
                    column's own 6.25rem plus the 5rem below it. */}
                <div className="flex w-full flex-col items-center gap-16 md:gap-[6.25rem]">
                    {/* The monogram's foot rests on the headline, so it shares
                        a gapless wrapper with the header rather than taking
                        the column's own gap. */}
                    <div className="flex w-full flex-col items-center">
                        <RevampLogomark />
                        <RevampHeader
                            {...header}
                            professionalName={professional?.title}
                            publishDate={publishDate}
                        />
                    </div>
                    {teaser && (
                        <div className="order-3 w-full md:order-none">
                            <RevampTeaser text={teaser} />
                        </div>
                    )}
                    {hero && (
                        <div className="order-2 w-full md:order-none md:mt-[5rem]">
                            <RevampHeroImage {...hero} />
                        </div>
                    )}
                </div>
            </>
        );
    };

    return (
        <main className="relative flex w-full flex-col items-center gap-16 bg-white pt-10 md:gap-[11.25rem] md:pt-12">
            {renderTitleSection()}
            {modules && modules.length > 0 && (
                <RevampModules
                    modules={modules}
                    professional={professional}
                    articleGatingEnabled={articleGatingEnabled}
                    // The slot sits inside the modules container, which gating
                    // clips and blurs, so the band would be unusable rather
                    // than merely below the fold. Omit it like the other
                    // post-body sections until the reader signs in.
                    afterFirstModule={
                        showNewsletterSignup && !articleGatingEnabled ? (
                            <RevampNewsletterSignup />
                        ) : undefined
                    }
                />
            )}
            {!articleGatingEnabled && (
                <>
                    <DetailScrollReveal>
                        <RevampThemesCredits
                            themes={header.categories}
                            credits={credits}
                            professional={professional}
                            downloads={downloads}
                            articleID={articleID}
                            articleTitle={header.title}
                            shareLink={shareLink}
                            enquiryProfessional={enquiryProfessional}
                            heroImage={hero?.imageUrl}
                        />
                    </DetailScrollReveal>
                    <DetailScrollReveal>
                        <RevampDiscoverMore tiles={discoverTiles} />
                    </DetailScrollReveal>
                    <DetailScrollReveal className="w-full">
                        <EditorialClosing />
                    </DetailScrollReveal>
                </>
            )}
        </main>
    );
};
